import React from 'react';
import { AlertTriangle, Activity, Shield, FileWarning } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { RisksStatistics200Response, RiskSeverityEnum, RiskStatusEnum } from 'api-client/models';

interface RiskStatisticsPanelProps {
  statistics: RisksStatistics200Response;
  isLoading: boolean;
  onRefresh?: () => void;
  title?: string;
}

// Helper function to get total count from a statistics object
const getTotalCount = (countObject?: { [key: string]: number }) => {
  if (!countObject) return 0;
  return Object.values(countObject).reduce((sum, count) => sum + count, 0);
};

// Helper function to format label (convert snake_case to Title Case)
const formatLabel = (label: string): string => {
  return label
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const RiskStatisticsPanel: React.FC<RiskStatisticsPanelProps> = ({
  statistics,
  isLoading,
  onRefresh,
  title = 'Risk Overview'
}) => {
  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold text-white">{title}</h2>
        {isLoading ? (
          <div className="text-sm text-gray-400 flex items-center">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mr-2"></div>
            Updating statistics...
          </div>
        ) : onRefresh && (
          <Button 
            variant="outline" 
            size="sm" 
            className="text-xs" 
            onClick={onRefresh}
          >
            Refresh Stats
          </Button>
        )}
      </div>

      {/* Main Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Severity Stats */}
        <div className="bg-[#0F1629] rounded-xl border border-gray-800/50 p-4 overflow-hidden">
          <h3 className="text-sm font-medium text-gray-400 mb-3 flex items-center">
            <AlertTriangle className="h-4 w-4 mr-2 text-yellow-500" />
            Risk Severity
          </h3>
          
          {isLoading ? (
            <div className="flex justify-center items-center h-32">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
            </div>
          ) : !statistics.severityCounts || Object.keys(statistics.severityCounts).length === 0 ? (
            <div className="text-center py-8 text-gray-500 text-sm">No severity data available</div>
          ) : (
            <div className="space-y-3">
              {Object.entries(statistics.severityCounts || {})
                .sort((a, b) => {
                  // Sort by severity level (critical first)
                  const severityOrder: Record<string, number> = { 'critical': 0, 'high': 1, 'medium': 2, 'low': 3 };
                  return (severityOrder[a[0].toLowerCase()] ?? 99) - (severityOrder[b[0].toLowerCase()] ?? 99);
                })
                .map(([severity, count]) => {
                  const totalSeverity = getTotalCount(statistics.severityCounts);
                  const percentage = totalSeverity > 0 ? Math.round((count / totalSeverity) * 100) : 0;
                  
                  // Color mapping
                  const colorMap: Record<string, string> = {
                    'critical': 'bg-red-500',
                    'high': 'bg-orange-500',
                    'medium': 'bg-yellow-500',
                    'low': 'bg-blue-500'
                  };
                  
                  const bgColor = colorMap[severity.toLowerCase()] || 'bg-gray-500';
                  
                  return (
                    <div key={severity} className="relative">
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="font-medium text-white">{formatLabel(severity)}</span>
                        <div className="flex items-center">
                          <span className="text-white font-medium">{count}</span>
                          <span className="text-gray-500 ml-1">({percentage}%)</span>
                        </div>
                      </div>
                      <div className="w-full bg-gray-800/50 rounded-full h-1.5 overflow-hidden">
                        <div 
                          className={`h-full rounded-full ${bgColor}`} 
                          style={{ width: `${percentage}%` }}
                        ></div>
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
        </div>
        
        {/* Status Stats */}
        <div className="bg-[#0F1629] rounded-xl border border-gray-800/50 p-4 overflow-hidden">
          <h3 className="text-sm font-medium text-gray-400 mb-3 flex items-center">
            <Activity className="h-4 w-4 mr-2 text-blue-500" />
            Risk Status
          </h3>
          
          {isLoading ? (
            <div className="flex justify-center items-center h-32">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
            </div>
          ) : !statistics.statusCounts || Object.keys(statistics.statusCounts).length === 0 ? (
            <div className="text-center py-8 text-gray-500 text-sm">No status data available</div>
          ) : (
            <div className="space-y-3">
              {Object.entries(statistics.statusCounts || {})
                .sort((a, b) => b[1] - a[1]) // Sort by count (highest first)
                .map(([status, count]) => {
                  const totalStatus = getTotalCount(statistics.statusCounts);
                  const percentage = totalStatus > 0 ? Math.round((count / totalStatus) * 100) : 0;
                  
                  // Status color mapping
                  const colorMap: Record<string, string> = {
                    'open': 'bg-blue-500',
                    'in_progress': 'bg-yellow-500',
                    'resolved': 'bg-green-500',
                    'false_positive': 'bg-purple-500',
                    'duplicate': 'bg-gray-500'
                  };
                  
                  const bgColor = colorMap[status.toLowerCase()] || 'bg-gray-500';
                  
                  return (
                    <div key={status} className="relative">
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="font-medium text-white">{formatLabel(status)}</span>
                        <div className="flex items-center">
                          <span className="text-white font-medium">{count}</span>
                          <span className="text-gray-500 ml-1">({percentage}%)</span>
                        </div>
                      </div>
                      <div className="w-full bg-gray-800/50 rounded-full h-1.5 overflow-hidden">
                        <div 
                          className={`h-full rounded-full ${bgColor}`} 
                          style={{ width: `${percentage}%` }}
                        ></div>
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
        </div>
        
        {/* Type Stats */}
        <div className="bg-[#0F1629] rounded-xl border border-gray-800/50 p-4 overflow-hidden">
          <h3 className="text-sm font-medium text-gray-400 mb-3 flex items-center">
            <Shield className="h-4 w-4 mr-2 text-indigo-500" />
            Risk Type
          </h3>
          
          {isLoading ? (
            <div className="flex justify-center items-center h-32">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
            </div>
          ) : !statistics.typeCounts || Object.keys(statistics.typeCounts).length === 0 ? (
            <div className="text-center py-8 text-gray-500 text-sm">No type data available</div>
          ) : (
            <div className="space-y-3">
              {Object.entries(statistics.typeCounts || {})
                .sort((a, b) => b[1] - a[1]) // Sort by count (highest first)
                .slice(0, 5) // Show top 5 types
                .map(([type, count]) => {
                  const totalType = getTotalCount(statistics.typeCounts);
                  const percentage = totalType > 0 ? Math.round((count / totalType) * 100) : 0;
                  
                  // Type color mapping with a variety of colors
                  const colorMap: Record<string, string> = {
                    'personal_info': 'bg-blue-500',
                    'credentials': 'bg-red-500',
                    'financial': 'bg-green-500',
                    'communication': 'bg-yellow-500',
                    'location': 'bg-purple-500'
                  };
                  
                  // Use a consistent color for unknown types
                  const bgColor = colorMap[type.toLowerCase()] || 'bg-indigo-500';
                  
                  return (
                    <div key={type} className="relative">
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="font-medium text-white">{formatLabel(type)}</span>
                        <div className="flex items-center">
                          <span className="text-white font-medium">{count}</span>
                          <span className="text-gray-500 ml-1">({percentage}%)</span>
                        </div>
                      </div>
                      <div className="w-full bg-gray-800/50 rounded-full h-1.5 overflow-hidden">
                        <div 
                          className={`h-full rounded-full ${bgColor}`} 
                          style={{ width: `${percentage}%` }}
                        ></div>
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
        </div>
        
        {/* Source Stats */}
        <div className="bg-[#0F1629] rounded-xl border border-gray-800/50 p-4 overflow-hidden">
          <h3 className="text-sm font-medium text-gray-400 mb-3 flex items-center">
            <FileWarning className="h-4 w-4 mr-2 text-pink-500" />
            Risk Source
          </h3>
          
          {isLoading ? (
            <div className="flex justify-center items-center h-32">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
            </div>
          ) : !statistics.sourceCounts || Object.keys(statistics.sourceCounts).length === 0 ? (
            <div className="text-center py-8 text-gray-500 text-sm">No source data available</div>
          ) : (
            <div className="space-y-3">
              {Object.entries(statistics.sourceCounts || {})
                .sort((a, b) => b[1] - a[1]) // Sort by count (highest first)
                .slice(0, 5) // Show top 5 sources
                .map(([source, count]) => {
                  const totalSource = getTotalCount(statistics.sourceCounts);
                  const percentage = totalSource > 0 ? Math.round((count / totalSource) * 100) : 0;
                  
                  // Source color mapping
                  const colorMap: Record<string, string> = {
                    'osint': 'bg-blue-500',
                    'dark_web': 'bg-purple-500',
                    'social_media': 'bg-pink-500',
                    'data_breach': 'bg-red-500',
                    'internal': 'bg-green-500'
                  };
                  
                  const bgColor = colorMap[source.toLowerCase()] || 'bg-pink-500';
                  
                  return (
                    <div key={source} className="relative">
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="font-medium text-white">{formatLabel(source)}</span>
                        <div className="flex items-center">
                          <span className="text-white font-medium">{count}</span>
                          <span className="text-gray-500 ml-1">({percentage}%)</span>
                        </div>
                      </div>
                      <div className="w-full bg-gray-800/50 rounded-full h-1.5 overflow-hidden">
                        <div 
                          className={`h-full rounded-full ${bgColor}`} 
                          style={{ width: `${percentage}%` }}
                        ></div>
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RiskStatisticsPanel;
